import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './trainedModel';
import { RootStoreType } from '../rootReducer';
import { AdvancedSettingsMap, AIRunModel, ModelDependency, TrainedModel } from '../../@shared/Models/Models';
import { ModelType } from '../../@shared/Models/ModelTypes';

const statesSlice = createSlice({
    name: 'trainedModelReducer',
    initialState: initialState.trainedModel,
    reducers: {
        setIsShowTrainedModelModal: (state, action: PayloadAction<boolean>) => {
            state.isShowModal = action.payload;
        },
        setIsShowRunTrainedModelModal: (state, action: PayloadAction<boolean>) => {
            state.isShowRunModal = action.payload;
        },
        setIsShowDuplicateModelModal: (state, action: PayloadAction<boolean>) => {
            state.isDuplicateModal = action.payload;
        },
        setRunData: (state, action: PayloadAction<string>) => {
            state.runData = action.payload;
        },
        setRunSchemaOrdering: (state, action: PayloadAction<string>) => {
            state.schemaOrdering = action.payload;
        },
        setRunSchemaOrderingResult: (state, action: PayloadAction<string>) => {
            state.schemaOrderingResult = action.payload;
        },
        setOnnxModalTrainerId: (state, action: PayloadAction<string>) => {
            state.onnxModalTrainerId = action.payload;
        },
        setOnnxUploadProgress: (state, action: PayloadAction<number>) => {
            state.onnxUploadProgress = action.payload;
        },
        setIsOnnxUploading: (state, action: PayloadAction<boolean>) => {
            state.isOnnxUploading = action.payload;
        },
        setRunResult: (state, action: PayloadAction<AIRunModel>) => {
            state.runResult = action.payload;
        },
        setTrainedModelName: (state, action: PayloadAction<string>) => {
            state.name = action.payload;
        },
        setTrainedModelDescription: (state, action: PayloadAction<string>) => {
            state.description = action.payload;
        },
        setTrainedModelSettings: (state, action: PayloadAction<string>) => {
            state.settings = action.payload;
        },
        setAiModelId: (state, action: PayloadAction<string>) => {
            state.aiModelId = action.payload;
        },
        setTestPercent: (state, action: PayloadAction<number>) => {
            state.testPercent = action.payload;
        },
        setAdvancedSettingsMap: (state, action: PayloadAction<AdvancedSettingsMap>) => {
            state.advancedSettingsMap = action.payload;
        },
        setAIModelTrainerTypeId: (state, action: PayloadAction<ModelType>) => {
            state.aiModelTrainerTypeId = action.payload;
        },
        setIsShowOnnxModal: (state, action: PayloadAction<boolean>) => {
            state.isShowOnnxModal = action.payload;
        },
        cleanTrainedModel: (state) => {
            state.isShowModal = false;
            state.isShowRunModal = false;
            state.runData = "";
            state.runResult = {} as AIRunModel;
            state.name = "";
            state.aiModelId = "";
            state.testPercent = 0.3;
            state.aiModelTrainerTypeId = undefined;
            state.aiModelTrainedModelId = undefined;
            state.openedModel = undefined;
            state.settings = "";
            state.modelDependencies = [];
        },
        setDuplicateModelId: (state, action: PayloadAction<string>) => {
            state.duplicateModelId = action.payload;
        },
        setAIModelTrainedModelId: (state, action: PayloadAction<string | undefined>) => {
            state.aiModelTrainedModelId = action.payload;
        },
        setOpenedModel: (state, action: PayloadAction<TrainedModel>) => {
            state.openedModel = action.payload;
        },
        setIsLoadingResults: (state, action: PayloadAction<boolean>) => {
            state.isLoadingResults = action.payload;
        },
        setIsModelUpdating: (state, action: PayloadAction<boolean>) => {
            state.isUpdating = action.payload;
        },
        setModelDependencies: (state, action: PayloadAction<ModelDependency[]>) => {
            state.modelDependencies = action.payload;
        },
        setModelDependency: (state, action: PayloadAction<ModelDependency>) => {
            const existing = state.modelDependencies.find(x => x.schemaItemId === action.payload.schemaItemId);
            if (existing) {
                existing.refModelId = action.payload.refModelId;
            } else {
                state.modelDependencies.push(action.payload);
            }
        },
        setIsShowCalculateMetricsModal: (state, action: PayloadAction<boolean>) => {
            state.isShowCalculateMetricsModal = action.payload;
        }
    }
});

export const {
    setIsShowTrainedModelModal,
    setAiModelId,
    setTestPercent,
    setTrainedModelName,
    setTrainedModelDescription,
    setTrainedModelSettings,
    setAIModelTrainerTypeId,
    cleanTrainedModel,
    setIsShowRunTrainedModelModal,
    setIsShowDuplicateModelModal,
    setDuplicateModelId,
    setRunData,
    setRunResult,
    setAdvancedSettingsMap,
    setOnnxModalTrainerId,
    setIsShowOnnxModal,
    setOnnxUploadProgress,
    setIsOnnxUploading,
    setRunSchemaOrdering,
    setRunSchemaOrderingResult,
    setAIModelTrainedModelId,
    setOpenedModel,
    setIsLoadingResults,
    setIsModelUpdating,
    setModelDependency,
    setModelDependencies,
    setIsShowCalculateMetricsModal
} = statesSlice.actions;

//selectors
export const getIsShowTrainedModelModal = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isShowModal;
export const getAiModelId = (store: RootStoreType): string => store.trainedModelState.trainedModel.aiModelId;
export const getTestPercent = (store: RootStoreType): number => store.trainedModelState.trainedModel.testPercent;
export const getTrainedModelName = (store: RootStoreType): string => store.trainedModelState.trainedModel.name;
export const getTrainedModelDescription = (store: RootStoreType): string => store.trainedModelState.trainedModel.description;
export const getTrainedModelSettings = (store: RootStoreType): string => store.trainedModelState.trainedModel.settings;
export const getAIModelTrainerTypeId = (store: RootStoreType): ModelType | undefined => store.trainedModelState.trainedModel.aiModelTrainerTypeId;
export const getAIModelTrainedModelId = (store: RootStoreType): string | undefined => store.trainedModelState.trainedModel.aiModelTrainedModelId;
export const getIsModelUpdating = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isUpdating;

export const getIsShowRunTrainedModelModal = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isShowRunModal;
export const getRunData = (store: RootStoreType): string => store.trainedModelState.trainedModel.runData;
export const getRunResult = (store: RootStoreType): AIRunModel => store.trainedModelState.trainedModel.runResult;
export const getAdvancedSettingsMap = (store: RootStoreType): AdvancedSettingsMap => store.trainedModelState.trainedModel.advancedSettingsMap;
export const getIsShowOnnxModal = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isShowOnnxModal;
export const getOnnxModalTrainerId = (store: RootStoreType): string => store.trainedModelState.trainedModel.onnxModalTrainerId;
export const getOnnxUploadProgress = (store: RootStoreType): number => store.trainedModelState.trainedModel.onnxUploadProgress;
export const getIsOnnxUploading = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isOnnxUploading;
export const getIsShowDuplicateModelModal = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isDuplicateModal;
export const getDuplicateModelId = (store: RootStoreType): string => store.trainedModelState.trainedModel.duplicateModelId;
export const getRunSchemaOrdering = (store: RootStoreType): string => store.trainedModelState.trainedModel.schemaOrdering;
export const getRunSchemaOrderingResult = (store: RootStoreType): string => store.trainedModelState.trainedModel.schemaOrderingResult;
export const getOpenedModel = (store: RootStoreType): TrainedModel | undefined => store.trainedModelState.trainedModel.openedModel;
export const getIsLoadingResults = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isLoadingResults;
export const getModelDependencies = (store: RootStoreType): ModelDependency[] => store.trainedModelState.trainedModel.modelDependencies;
export const getIsShowCalculateMetricsModal = (store: RootStoreType): boolean => store.trainedModelState.trainedModel.isShowCalculateMetricsModal;

export default statesSlice.reducer;